import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { worldcupAPI, predictionsAPI } from '../services/api';
import { MapPin, Calendar, Trophy, Users, History } from 'lucide-react';
import Map from '../components/Map';
import SEOHead from '../components/SEOHead';
import { getEventSchema, getFAQSchema } from '../utils/seoSchemas';

const MatchDetail = () => {
  const { id } = useParams();
  const { data: match, isLoading: matchLoading, error: matchError } = useQuery({
    queryKey: ['worldcupMatch', id],
    queryFn: () => worldcupAPI.getMatch(id).then((r) => r.data),
    enabled: !!id,
  });

  const { data: prediction } = useQuery({
    queryKey: ['prediction', id],
    queryFn: () => predictionsAPI.winProbability(id).then((r) => r.data),
    enabled: !!id && !!match,
  });

  const team1 = match?.team1_name || match?.home_team;
  const team2 = match?.team2_name || match?.away_team;
  const canH2H = team1 && team2 && !team1.includes('To be announced') && !team2.includes('To be announced');

  const { data: h2h } = useQuery({
    queryKey: ['h2h', team1, team2],
    queryFn: () => predictionsAPI.h2h(team1, team2).then((r) => r.data),
    enabled: !!canH2H,
  });

  const stadium = match?.stadium_name || match?.location;
  const venueLine = match ? [match.stadium_city, match.stadium_country].filter(Boolean).join(', ') : '';

  const matchFaq = useMemo(
    () => {
      if (!match) {
        return [
          { question: 'When is this match?', answer: 'Loading match details.' },
          { question: 'How do I get tickets?', answer: 'Buy tickets only from FIFA and authorized sellers.' },
          { question: 'How should I plan travel?', answer: 'Use our host city guides and travel planner.' },
        ];
      }
      return [
        {
          question: `When and where is ${team1 || 'TBD'} vs ${team2 || 'TBD'}?`,
          answer: match.date
            ? `Kickoff is listed as ${new Date(match.date).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })} at ${stadium || 'the assigned venue'}.${venueLine ? ` Location: ${venueLine}.` : ''} Always confirm the official FIFA schedule before booking travel.`
            : 'Kickoff time and venue assignments can change. Check FIFA and your ticket for the official schedule before you travel.',
        },
        {
          question: 'How do I get tickets for this World Cup match?',
          answer: 'Buy tickets only from FIFA and authorized sellers. Third-party resellers can be fraudulent or invalid. Start from our tickets page, then complete purchase on official channels.',
        },
        {
          question: 'How should I plan travel to the stadium?',
          answer: 'Use our host city guides and travel planner for flights and hotels. On match day, plan extra time for security lines, transit crowding, and road closures near the venue.',
        },
      ];
    },
    [match, team1, team2, stadium, venueLine],
  );

  const metaDescription = useMemo(() => {
    if (!match) return '2026 FIFA World Cup match details, venue, and fan planning.';
    const parts = [
      `2026 FIFA World Cup: ${team1 || 'TBD'} vs ${team2 || 'TBD'}`,
      match.date ? new Date(match.date).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null,
      stadium,
      venueLine,
    ].filter(Boolean);
    return parts.join(' · ').slice(0, 160);
  }, [match, team1, team2, stadium, venueLine]);

  if (matchLoading || !id) return <div className="p-8 text-center">Loading...</div>;
  if (matchError || !match) return <div className="p-8 text-center text-red-600 dark:text-red-400">Match not found.</div>;

  const stadiumSlug = match.stadium_slug;
  const roundLabel = match.round_number === 1 ? 'Group stage' : match.round_number === 2 ? 'Group stage' : match.round_number <= 4 ? 'Round of 32' : match.round_number <= 6 ? 'Quarter-finals' : match.round_number === 7 ? 'Semi-finals' : 'Final';

  return (
    <>
      <SEOHead
        title={`${team1 || 'TBD'} vs ${team2 || 'TBD'} – Match ${match.match_number || id} | FIFA World Cup 2026`}
        description={metaDescription}
        schema={[getEventSchema(match), getFAQSchema(matchFaq)]}
        ogType="article"
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Match {match.match_number} · {roundLabel}{match.group_stage ? ` · ${match.group_stage}` : ''}</p>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {team1 || 'TBD'} <span className="text-amber-500 mx-2">vs</span> {team2 || 'TBD'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Schedules and venues are for planning only—verify details on{' '}
            <a href="https://www.fifa.com" className="text-amber-600 dark:text-amber-400 hover:underline" target="_blank" rel="noopener noreferrer">
              FIFA.com
            </a>{' '}
            before you book.
          </p>
          {match.date && (
            <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {new Date(match.date).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="p-6 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Win probability
            </h2>
            {prediction?.winProbability ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{team1 || 'Home'}</span>
                  <span className="text-amber-500 font-bold">{prediction.winProbability.team1}%</span>
                </div>
                <div className="h-3 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
                  <div className="bg-amber-500 h-full" style={{ width: `${prediction.winProbability.team1}%` }} />
                  <div className="bg-gray-400 dark:bg-gray-600 h-full" style={{ width: `${prediction.winProbability.team2}%` }} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">{team2 || 'Away'}</span>
                  <span className="text-gray-500 font-bold">{prediction.winProbability.team2}%</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">Prediction based on FIFA rankings when available.</p>
            )}
          </div>

          <div className="p-6 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-500" />
              Venue
            </h2>
            {stadium && (
              <>
                <p className="font-medium text-gray-900 dark:text-white">{stadium}</p>
                {(match.stadium_city || match.stadium_country) && (
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    {[match.stadium_city, match.stadium_country].filter(Boolean).join(', ')}
                  </p>
                )}
                {match.stadium_capacity && (
                  <p className="text-amber-600 dark:text-amber-400 text-sm mt-1">Capacity: {Number(match.stadium_capacity).toLocaleString()}</p>
                )}
                {stadiumSlug && (
                  <Link
                    to={`/stadiums/${stadiumSlug}`}
                    className="inline-block mt-3 text-amber-600 dark:text-amber-400 hover:underline font-medium"
                  >
                    View stadium details →
                  </Link>
                )}
                {match.stadium_latitude != null && match.stadium_longitude != null && (
                  <div className="mt-4 h-48 rounded-lg overflow-hidden">
                    <Map
                      markers={[{
                        lat: parseFloat(match.stadium_latitude),
                        lng: parseFloat(match.stadium_longitude),
                        label: stadium,
                      }]}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="p-6 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-amber-500" />
            Team lineups
          </h2>
          <p className="text-gray-500 dark:text-gray-400">Lineups will be announced closer to the match.</p>
        </div>

        {canH2H && (
          <div className="p-6 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <History className="h-5 w-5 text-amber-500" />
              Head to head (FIFA matches)
            </h2>
            {h2h && (h2h.matches?.length > 0 || h2h.team1Wins !== undefined) ? (
              <>
                <div className="flex flex-wrap gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-500">{h2h.team1Wins ?? 0}</p>
                    <p className="text-sm text-gray-500">{team1} wins</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-500">{h2h.draws ?? 0}</p>
                    <p className="text-sm text-gray-500">Draws</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-500">{h2h.team2Wins ?? 0}</p>
                    <p className="text-sm text-gray-500">{team2} wins</p>
                  </div>
                </div>
                {h2h.matches?.length > 0 && (
                  <ul className="space-y-2 text-sm">
                    {h2h.matches.slice(0, 10).map((m, i) => (
                      <li key={i} className="text-gray-600 dark:text-gray-400">
                        {m.team1_name} {m.score_team1} – {m.score_team2} {m.team2_name}
                        {m.date && <span className="ml-2 text-gray-400">({new Date(m.date).getFullYear()})</span>}
                      </li>
                    ))}
                    {h2h.matches.length > 10 && <li className="text-gray-400">… and {h2h.matches.length - 10} more</li>}
                  </ul>
                )}
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No previous FIFA meetings between these teams in our records.</p>
            )}
          </div>
        )}

        <section className="mt-10 p-5 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700" aria-labelledby="match-faq-heading">
          <h2 id="match-faq-heading" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Match planning FAQ
          </h2>
          <dl className="space-y-4">
            {matchFaq.map(({ question, answer }) => (
              <div key={question}>
                <dt className="font-medium text-gray-900 dark:text-white">{question}</dt>
                <dd className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/matches" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">← All matches</Link>
          <Link to="/cities" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">Host cities</Link>
          <Link to="/travel-planner" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">Travel planner</Link>
        </div>
      </div>
    </>
  );
};

export default MatchDetail;
